
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCaresathiData } from './Caresathiapi';
import IntroSection from './IntroSection';
import CurebaySolutions from '../../Shared/CurebaySolutions/CurebaySolutions';
import ThirdSection from '../../Shared/ThirdSection/ThirdSection';
import CheckHealthSteps from '../../Shared/CheckHealthSteps/CheckHealthSteps';
import CaresathiCreate from "./CaresathiCreate";
import SubscriptionDialog from './SubscriptionDialog';
import SubscriptionNudge from './SubscriptionNudge';
import { usePatientData } from "../../hooks/usePatientData";
import { FeedbackModal, useServicePageFeedback } from '../Home/FeedbackSystem';

import LoginToast from '../../MainLayout/ToastifyLogin';
import { LoginContext } from '../../MainLayout/MainLayout';
import { setRedirectPath } from '../../auth/utils/storage';

// Same side/vertical spacing as the shared sections (ThirdSection, CheckHealthSteps,
// CurebaySolutions) so every heading on the page starts at the same left edge.
const SECTION_PAD = 'py-8 px-[14px] md:py-10 md:px-7 min-[1101px]:py-12 min-[1101px]:px-10 xl:py-14 xl:px-[max(60px,calc(50%_-_660px))]';

// Section headings per Figma: first part brand blue, the rest brand green.
// "What You Get with CureBay CareSathi?" → split before "with"
// "Your Questions, Answered (FAQ)"       → split after the comma
// "Why trust us?"                         → split after "Why"
// anything else                           → last word green
const splitHeading = (heading = '') => {
  const text = heading.trim();
  const comma = text.indexOf(', ');
  if (comma > 0) return [text.slice(0, comma + 1), text.slice(comma + 2)];
  const withIdx = text.search(/\swith\s/i);
  if (withIdx > 0) return [text.slice(0, withIdx), text.slice(withIdx + 1)];
  const why = text.match(/^(why)\s+(.+)$/i);
  if (why) return [why[1], why[2]];
  const words = text.split(' ');
  const last = words.length > 1 ? words.pop() : '';
  return [words.join(' '), last];
};

const SectionHeading = ({ text, className = '' }) => {
  const [main, accent] = splitHeading(text);
  return (
    <h2
      className={`font-bold leading-[1.3] text-[26px] md:text-[30px] min-[1101px]:text-[36px] xl:text-[40px] ${className}`}
      style={{ color: 'var(--color-brand-primary)' }}
    >
      {main}{accent && ' '}
      {accent && <span style={{ color: 'var(--color-brand-green)' }}>{accent}</span>}
    </h2>
  );
};

const SectionSubheading = ({ children }) => (
  <p
    className="font-medium leading-[1.4] text-[15px] md:text-[17px] xl:text-[20px] mb-6 md:mb-8"
    style={{ color: 'var(--color-text-subheading)' }}
  >
    {children}
  </p>
);

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.61 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.09a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
);

const GiftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 12 20 22 4 22 4 12"/>
    <rect x="2" y="7" width="20" height="5"/>
    <line x1="12" y1="22" x2="12" y2="7"/>
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
  </svg>
);

const BotIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v4"/>
    <line x1="8" y1="16" x2="8" y2="16"/>
    <line x1="16" y1="16" x2="16" y2="16"/>
  </svg>
);

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const Caresathi = () => {
    const { openLogin } = useContext(LoginContext);
  const storedData1 = JSON.parse(localStorage.getItem("loginObj"));
  const userData = storedData1;
  const { showFeedback, closeFeedback } = useServicePageFeedback(userData?.code, 'Care Sathi');
    const loginToastRef = useRef(null);
  const [attrs, setAttrs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSmartOpen, setIsSmartOpen] = useState(false);
  const [seventhIdx, setSeventhIdx] = useState(0);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [faqTabIdx, setFaqTabIdx] = useState(0);
  const [openFaqId, setOpenFaqId] = useState(null);

  // ── Subscription state ──────────────────────────────────────────────────────
  const [subscribeVisible, setSubscribeVisible]       = useState(false);
  const [expiryStatus, setExpiryStatus]               = useState('');
  const [upcomingCount, setUpcomingCount]             = useState(0);
  const [hasLatestSubscription, setHasLatestSubscription] = useState(false);

  const toggleFlip = (id) => {
    setFlippedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const [uploadedImage, setUploadedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const toastRef = useRef(null);
  
  const [report_count, setReportCount] = useState(null);
  const endpointBase = process.env.REACT_APP_REPORTCOUNTURL;
  const countApiUrl = `${endpointBase}caresathi/count`;
  const { patientData } = usePatientData();

const handleCardClick = (item) => {
  const subheading = item?.subheading?.toLowerCase() || "";

  if (subheading === "visioncare") {
    navigate("/visioncare");
    return;
  }

  if (subheading === "caresathi") {
    navigate("/caresathi");
  } else if (subheading === "skincare") {
    navigate("/skincare");
  } else if (subheading === "smartvitals") {
    navigate("/smartvital");
  } else if (subheading === "oralcare") {
    navigate("/oralcare");
  } else if (subheading === "lungscare") {
    navigate("/lungscare");
  } else if (subheading === "smart report") {
    navigate("/smartreport");
  } else if (subheading === "symptomchecker") {
    navigate("/symptomchecker");
  } else if (subheading === "smart diet") {
    navigate("/smartdiet");
  } else {
    console.warn("No route found for:", subheading);
  }
};

   const handleOpen = (e) => {
    if (!userData?.code) {
      e.preventDefault();
      openLogin();
      return;
    }
    setIsSmartOpen(true);
  };

  const handleClose = () => {
    setIsSmartOpen(false);
    setUploadedImage(null);
    setPdfUrl(null);
  };

  const handleSmartClose = () => {
  setIsSmartOpen(false);
  setUploadedImage(null);
  setPdfUrl(null);
};

 const handleSecondButton = () => {
  navigate("/smartreportlist");
};

  // ── Fetch patient subscription status on mount ──────────────────────────────
  useEffect(() => {
    const patientCode = userData?.code;
    if (!patientCode) return;

    const fetchPatientSubscription = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_CARE_SATHI}ds/subscription/patient/${patientCode}/status?status=1`
        );
        const subs = res?.data?.data?.subscriptions || res?.data?.data || [];
        if (Array.isArray(subs) && subs.length > 0) {
          setExpiryStatus(subs[0]?.expiryStatus || '');
          setHasLatestSubscription(true);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
      }
    };

    const fetchUpcomingCount = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_CARE_SATHI}ds/subscription/patient/${patientCode}/status?status=2`
        );
        if (res?.data?.success && res?.data?.data) {
          const { summary, has_active_subscription } = res.data.data;
          if (summary) {
            setUpcomingCount(summary.upcoming_count || 0);
          }
          const totalSubscriptions =
            (summary?.active_count || 0) +
            (summary?.upcoming_count || 0) +
            (summary?.pending_count || 0) +
            (summary?.completed_count || 0);
          if (has_active_subscription === true || totalSubscriptions > 0) {
            setHasLatestSubscription(true);
          }
        }
      } catch (err) {
        console.error('Error fetching upcoming subscriptions:', err);
      }
    };

    fetchPatientSubscription();
    fetchUpcomingCount();
  }, [userData?.code]);

  const handleSubscribe = () => {
    if (!userData?.code) {
      setRedirectPath('/caresathi');
      openLogin();
      return;
    }
    setSubscribeVisible(true);
  };

  const handleAskCaresathi = () => {
    if (!userData?.code) {
      setRedirectPath('/ask-caresathi');
      openLogin();
      return;
    }
    navigate("/ask-caresathi");
  };

  const handleReferEarn = () => {
    window.open(process.env.REACT_APP_REFER_EARN_URL, '_blank', 'noopener,noreferrer');
  };

  const phoneNumber = (process.env.REACT_APP_WHATSAPP_DCS || '').trim();

  const handleCallUs = () => {
    if (navigator.userAgent.match(/iPhone|Android/i)) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      navigator.clipboard.writeText(phoneNumber)
        .then(() => {
          toastRef.current?.show({ severity: 'success', summary: 'Copied!', detail: 'Phone number copied to clipboard.', life: 3000 });
        })
        .catch(() => {
          toastRef.current?.show({ severity: 'error', summary: 'Failed', detail: 'Failed to copy the phone number. Try again.', life: 3000 });
        });
    }
  };

  const navigate = useNavigate();

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 3000,
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };


  useEffect(() => {
    let isMounted = true;
    getCaresathiData()
      .then(res => {
        if (!isMounted) return;
        const entry = res.data?.data?.[0]?.attributes;
        setAttrs(entry || {});
      })
      .catch(err => {
        if (!isMounted) return;
        setError(err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  // Auto-open the first FAQ question whenever data loads or tab changes
  useEffect(() => {
    const tabs = attrs?.eight?.[0]?.subheading || [];
    const activeQuestions = (tabs[faqTabIdx]?.subcomp || []).filter((q) => q.question);
    if (activeQuestions.length > 0) {
      setOpenFaqId(activeQuestions[0].id);
    } else {
      setOpenFaqId(null);
    }
  }, [attrs, faqTabIdx]);

  useEffect(() => {
    if (!countApiUrl) return;
  
    const fetchCount = async () => {
      try {
        const res = await fetch(countApiUrl);
        const data = await res.json();
        setReportCount(data.session_count ?? null);
      } catch (err) {
        console.error("Failed to fetch report count:", err);
      }
    };
  
    fetchCount();
  }, [countApiUrl]);

  if (loading) return <div className="p-8 text-center">Loading…</div>;
  if (error) return <div className="p-8 text-center text-red-600">Error loading data.</div>;
  if (!attrs) return <div className="p-8 text-center">No data</div>;

  const firstcompItem = attrs.first?.[0] || {};
  const heading = firstcompItem.heading || '';
  const subheading = firstcompItem.subheading || '';
  const description = firstcompItem.description || '';
  const image = firstcompItem.imgurl || '';

  const firstSectionItems = [
    {
      heading,
      subheading,
      description,
      onButtonClick: handleOpen,
      onSecondButtonClick: handleSecondButton,
      image,
    },
  ];

  // Map API buttons array to action handlers by label
  const buttonHandlerMap = {
    'Subscribe Now':       { variant: 'subscribe', icon: StarIcon,      onClick: handleSubscribe },
    'Ask CareSathi':       { variant: 'ask',       icon: BotIcon,       onClick: handleAskCaresathi },
    'Call Us':             { variant: 'call',      icon: PhoneIcon,     onClick: handleCallUs },
    'Chat via WhatsApp':   { variant: 'whatsapp',  icon: WhatsAppIcon,  href: `https://wa.me/91${process.env.REACT_APP_WHATSAPP_DCS}`, target: '_blank' },
  };

  const apiButtons = firstcompItem.buttons || [];
  const actionButtons = [
    ...apiButtons.map((b) => {
      const mapped = buttonHandlerMap[b.button] || { variant: 'outline' };
      // Rename "Subscribe Now" → "Renew Now" when user already has the latest subscription
      const label = (b.button === 'Subscribe Now' && hasLatestSubscription) ? 'Renew Now' : b.button;
      return { label, ...mapped };
    }),
    { label: 'Refer & Earn', variant: 'refer', icon: GiftIcon, onClick: handleReferEarn },
  ];

  const thirdSection    = attrs.third?.[0]      || {};
  const fourthSection   = attrs.fourth?.[0]     || {};
  const lifechanges     = attrs.lifechanges?.[0] || {};
  const sixthSection    = attrs.sixth?.[0]      || {};
  const seventhSection  = attrs.seventh?.[0]    || {};
  const seventhSectionItems = seventhSection?.seventhsub || [];
  const stats           = attrs.stats?.[0]      || {};
  const subscribeData   = attrs.subscribe?.[0]  || {};
  const faqSection      = attrs.eight?.[0]      || {};

return (
  <>
    <LoginToast ref={loginToastRef} />
    <Toast ref={toastRef} position="top-right" />

    {/* Subscription expiry nudge */}
    {expiryStatus && upcomingCount < 2 && (
      <SubscriptionNudge expiryStatus={expiryStatus} />
    )}
    
    <section className="pb-6 md:pb-0">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#42BA85]/8 to-transparent rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-32 right-16 w-96 h-96 bg-gradient-to-tr from-[#F3F8FF]/10 to-transparent rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
        <div
          className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-br from-[#42BA85]/6 to-transparent rounded-full blur-2xl animate-bounce"
          style={{ animationDelay: '2s', animationDuration: '3s' }}
        ></div>
      </div>

      <IntroSection
        items={firstSectionItems}
        report_count={report_count}
        actionButtons={actionButtons}
      />

      {Object.keys(thirdSection).length > 0 && (
        <ThirdSection data={thirdSection} />
      )}

      {Object.keys(fourthSection).length > 0 && (
        <CheckHealthSteps data={fourthSection} onButtonClick={handleOpen} mediaAlign="start" />
      )}

      {/* Life Changes section */}
      {Object.keys(lifechanges).length > 0 && (
        <section className={`${SECTION_PAD} bg-[#F9F9F9]`}>
          <div className="w-full">
            {/* Heading */}
            <SectionHeading text={lifechanges.heading} className={lifechanges.subheading ? 'mb-2' : 'mb-6 md:mb-8'} />
            {lifechanges.subheading && (
              <SectionSubheading>{lifechanges.subheading.trim()}</SectionSubheading>
            )}

            {/* Mobile: image on top, Before/After below stacked
                Desktop: 3-column Before | Image | After */}
            <div className="flex flex-col md:grid md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 md:items-center">

              {/* ── Before — order-2 on mobile (appears after image) ── */}
              <div className="order-2 md:order-none">
                <div className="flex items-center gap-2 mb-3 md:mb-5">
                  <img src={lifechanges.beforeicon} alt="Before Icon" className="w-6 h-6 mr-3" />
                  <span className="font-bold text-base md:text-[18px] text-[#222]">{lifechanges.subheading1 || 'Before'}</span>
                </div>
                <div className="flex flex-col gap-2 md:gap-3">
                  {(lifechanges.beforepoints || []).map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 bg-white rounded-xl px-3 md:px-4 py-2.5 md:py-3 border border-gray-200 shadow-sm"
                    >
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-100 flex-shrink-0 flex items-center justify-center">
                      <img src={p.iconurl} alt="Before Icon" className="w-6 h-6" />
                      </div>
                      <span className="text-xs md:text-sm text-gray-700 leading-snug">{p.points}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Center image — order-1 on mobile (appears first) ── */}
              <div className="order-1 md:order-none flex flex-col items-center gap-3 md:gap-4 px-4">
                <div className="w-[160px] h-[160px] md:w-[260px] md:h-[260px] rounded-full flex-shrink-0">
                  <div className="w-full h-full rounded-full overflow-hidden">
                    {lifechanges.imgurl ? (
                      <img src={lifechanges.imgurl} alt="CareSathi" className="w-full h-full object-contain" />
                    ) : (
                      <div
                        className="w-full h-full"
                        style={{ background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)' }}
                      />
                    )}
                  </div>
                </div>
                {lifechanges.description && (
                  <div className="border border-gray-300 rounded-full px-4 md:px-6 py-1.5 md:py-2 text-center text-xs md:text-sm italic text-gray-600 bg-white shadow-sm max-w-[190px]">
                    {lifechanges.description}
                  </div>
                )}
              </div>

              {/* ── After — order-3 on mobile ── */}
              <div className="order-3 md:order-none">
                <div className="flex items-center gap-2 mb-3 md:mb-5">
                <img src={lifechanges.aftericon} alt="Before Icon" className="w-6 h-6 mr-3" />
                  <span className="font-bold text-base md:text-[18px]" style={{ color: 'var(--color-brand-green)' }}>{lifechanges.subheading2 || 'After'}</span>
                </div>
                <div className="flex flex-col gap-2 md:gap-3">
                  {(lifechanges.afterpoints || []).map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 bg-white rounded-xl px-3 md:px-4 py-2.5 md:py-3 border border-[#DFF1FB] shadow-sm"
                    >
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#EAF3FA] flex-shrink-0 flex items-center justify-center">
                      <img src={p.iconurl} alt="Before Icon" className="w-6 h-6" />
                      </div>
                      <span className="text-xs md:text-sm text-gray-700 leading-snug">{p.points}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* What You Get section */}
      {seventhSectionItems.length > 0 && (() => {
        const visibleCount = 3;
        const mobileVisibleCount = 1;
        const maxIdx     = Math.max(0, seventhSectionItems.length - visibleCount);
        const mobileMaxIdx = Math.max(0, seventhSectionItems.length - mobileVisibleCount);
        const visible    = seventhSectionItems.slice(seventhIdx, seventhIdx + visibleCount);
        const mobileVisible = seventhSectionItems.slice(seventhIdx, seventhIdx + mobileVisibleCount);
        return (
          <section
            className={SECTION_PAD}
            style={{ backgroundColor: '#fff', backgroundImage: 'var(--section-bg-gradient)' }}
          >
            <div className="w-full">
              {/* Heading */}
              <SectionHeading text={seventhSection.heading} className="mb-2" />
              <SectionSubheading>All services. One subscription. Zero stress.</SectionSubheading>

              {/* Flip animation styles */}
              <style>{`
                .flip-card { perspective: 1500px; }
                .flip-inner {
                  position: relative;
                  width: 100%;
                  height: 100%;
                  transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
                  transform-style: preserve-3d;
                }
                .flip-inner.is-flipped { transform: rotateY(180deg); }
                .flip-face {
                  position: absolute;
                  inset: 0;
                  -webkit-backface-visibility: hidden;
                  backface-visibility: hidden;
                  border-radius: 1rem;
                  overflow: hidden;
                }
                .flip-back { transform: rotateY(180deg); }
                /* Front stays in normal flow so the card is as tall as its content (photo keeps its
                   own aspect ratio); the back overlays it at the same size. */
                .flip-face.flip-front { position: relative; inset: auto; height: 100%; }
                /* Figma card look: light blue border + soft glow */
                .wyg-card {
                  background: #ffffff;
                  border: 1px solid #C9EEF8;
                  box-shadow: 0px 4px 12px 0px #80DAF14D;
                }
              `}</style>

              {/* Cards — desktop: 3 at a time, mobile: 1 at a time */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visible.map((item) => {
                  const flipped = flippedCards.has(item.id);
                  return (
                    <div key={item.id} className="flip-card">
                      <div className={`flip-inner ${flipped ? 'is-flipped' : ''}`}>

                        {/* ── FRONT ── */}
                        <div className="flip-face flip-front wyg-card flex flex-col">
                          {/* Image */}
                          <div className="relative w-full aspect-[390/229] bg-gray-100 flex-shrink-0">
                            {item.imgurl ? (
                              <img src={item.imgurl} alt={item.subheading} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full" style={{ background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)' }} />
                            )}
                            {/* Overlapping icon badge */}
                            <div
                              className="absolute bottom-0 left-3 translate-y-[54%] z-10"
                              // style={{ background: 'var(--primary-gradient-135)' }}
                            >
                              {item.iconurl ? (
                                <img src={item.iconurl} alt="" className="block w-[84px] h-[84px] xl:w-[92px] xl:h-[92px] object-contain" />
                              ) : (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                                  <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 8v4l3 3"/>
                                </svg>
                              )}
                            </div>
                          </div>

                          <div className="pt-[50px] px-5 pb-5 flex flex-col flex-1">
                            <h3
                              className="font-semibold text-[17px] leading-snug mb-1.5"
                              style={{ color: 'var(--color-brand-primary)' }}
                            >
                              {item.subheading}
                            </h3>
                            <p className="text-[13px] text-gray-500 leading-[1.5] line-clamp-2">{item.description}</p>
                            <button
                              onClick={() => toggleFlip(item.id)}
                              className="mt-auto pt-3 flex items-center gap-1 text-[13px] font-semibold self-end underline"
                              style={{ color: 'var(--color-brand-primary)' }}
                            >
                              Read more
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* ── BACK ── */}
                        <div className="flip-face flip-back wyg-card flex flex-col p-5">
                          <h3
                            className="font-bold text-[20px] leading-[1.2] mb-4"
                            style={{ color: 'var(--color-brand-primary)' }}
                          >
                            {item.subheading}.
                          </h3>

                          <ul className="flex flex-col gap-2.5 flex-1 overflow-y-auto">
                            {(item.points || []).map((pt) => (
                              <li key={pt.id} className="flex items-center gap-3">
                                <div
                                  className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm"
                                  style={{ background: 'var(--primary-gradient-135)' }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                </div>
                                <span className="text-[14px] text-[#3B3B3B] font-medium">{pt.points}</span>
                              </li>
                            ))}
                          </ul>

                          <button
                            onClick={() => toggleFlip(item.id)}
                            className="mt-4 flex items-center gap-1 text-sm font-semibold self-end underline"
                            style={{ color: 'var(--color-brand-primary)' }}
                          >
                            Read less
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
                            </svg>
                          </button>
                        </div>

                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile: single card with arrows */}
              <div className="md:hidden">
                {mobileVisible.map((item) => {
                  const flipped = flippedCards.has(item.id);
                  return (
                    <div key={item.id} className="flip-card">
                      <div className={`flip-inner ${flipped ? 'is-flipped' : ''}`}>
                        {/* FRONT */}
                        <div className="flip-face flip-front wyg-card flex flex-col">
                          <div className="relative w-full aspect-[390/229] bg-gray-100 flex-shrink-0">
                            {item.imgurl ? (
                              <img src={item.imgurl} alt={item.subheading} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full" style={{ background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)' }} />
                            )}
                            <div
                              className="absolute bottom-0 left-3 translate-y-[54%] z-10"
                              // style={{ background: 'var(--primary-gradient-135)' }}
                            >
                              {item.iconurl ? (
                                <img src={item.iconurl} alt="" className="block w-[84px] h-[84px] xl:w-[92px] xl:h-[92px] object-contain" />
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                                  <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 8v4l3 3"/>
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="pt-[46px] px-4 pb-4 flex flex-col flex-1">
                            <h3 className="font-semibold text-[16px] leading-snug mb-1.5" style={{ color: 'var(--color-brand-primary)' }}>
                              {item.subheading}
                            </h3>
                            <p className="text-[13px] text-gray-500 leading-[1.5] line-clamp-2">{item.description}</p>
                            <button onClick={() => toggleFlip(item.id)} className="mt-3 flex items-center gap-1 text-xs font-semibold self-end underline" style={{ color: 'var(--color-brand-primary)' }}>
                              Read more
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                            </button>
                          </div>
                        </div>
                        {/* BACK */}
                        <div className="flip-face flip-back wyg-card flex flex-col p-4">
                          <h3 className="font-bold text-[18px] leading-[1.2] mb-3" style={{ color: 'var(--color-brand-primary)' }}>
                            {item.subheading}.
                          </h3>
                          <ul className="flex flex-col gap-2 flex-1 overflow-y-auto">
                            {(item.points || []).map((pt) => (
                              <li key={pt.id} className="flex items-center gap-2.5">
                                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm" style={{ background: 'var(--primary-gradient-135)' }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                </div>
                                <span className="text-[13px] text-[#3B3B3B] font-medium">{pt.points}</span>
                              </li>
                            ))}
                          </ul>
                          <button onClick={() => toggleFlip(item.id)} className="mt-3 flex items-center gap-1 text-xs font-semibold self-end underline" style={{ color: 'var(--color-brand-primary)' }}>
                            Read less
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Mobile nav arrows */}
                <div className="flex items-center justify-between mt-5">
                  <button
                    onClick={() => setSeventhIdx((i) => Math.max(0, i - 1))}
                    disabled={seventhIdx === 0}
                    className="w-9 h-9 rounded-full flex items-center justify-center border transition-opacity disabled:opacity-30"
                    style={{ borderColor: 'var(--color-brand-primary)', color: 'var(--color-brand-primary)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span className="text-xs text-gray-400">{seventhIdx + 1} / {seventhSectionItems.length}</span>
                  <button
                    onClick={() => setSeventhIdx((i) => Math.min(mobileMaxIdx, i + 1))}
                    disabled={seventhIdx >= mobileMaxIdx}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity disabled:opacity-30"
                    style={{ background: 'var(--color-brand-primary)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              </div>

              {/* Desktop navigation arrows */}
              <div className="hidden md:flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setSeventhIdx((i) => Math.max(0, i - 1))}
                  disabled={seventhIdx === 0}
                  className="w-10 h-10 rounded-full flex items-center justify-center border transition-opacity disabled:opacity-30"
                  style={{ borderColor: 'var(--color-brand-primary)', color: 'var(--color-brand-primary)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button
                  onClick={() => setSeventhIdx((i) => Math.min(maxIdx, i + 1))}
                  disabled={seventhIdx >= maxIdx}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-opacity disabled:opacity-30"
                  style={{ background: 'var(--color-brand-primary)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Stats + Subscribe section */}
      {(Object.keys(stats).length > 0 || Object.keys(subscribeData).length > 0) && (
        <section className={`${SECTION_PAD} bg-white`}>
          <div className="w-full space-y-8 md:space-y-10">

            {/* ── Why trust us? ── */}
            {(stats.sub || []).length > 0 && (
              <div>
                <SectionHeading text={stats.heading || 'Why trust us?'} className="mb-5 md:mb-8" />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                  {(stats.sub || []).map((s) => (
                    <div
                      key={s.id}
                      className="flex flex-col items-center gap-2.5 md:gap-3 rounded-2xl py-4 md:py-5 px-2 md:px-3 bg-white border border-[#BDEAF7]"
                      style={{ boxShadow: '0px 0px 12px 0px #80DAF180' }}
                    >
                      <div className="w-14 h-14 md:w-[76px] md:h-[76px] rounded-full bg-[#DFF1FB] flex items-center justify-center">
                        <span className="text-[15px] md:text-[20px] font-semibold text-[#2B2B2B]">{s.number}</span>
                      </div>
                      <p className="text-center text-[12px] md:text-[14px] font-medium text-[#3A3A3A] leading-snug">{s.subheading}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Subscribe CTA card ── */}
            {Object.keys(subscribeData).length > 0 && (
              <div
                className="rounded-2xl md:rounded-3xl px-4 py-4 md:px-6 md:py-4 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 relative overflow-hidden bg-white border border-[#BDEAF7]"
                style={{ boxShadow: '0px 0px 12px 0px #80DAF180' }}
              >
                {/* Doctor illustration — hidden on mobile to save space */}
                {subscribeData.imgurl && (
                  <div className="hidden md:flex flex-shrink-0 w-[120px] h-[105px] xl:w-[140px] xl:h-[120px] items-center justify-center">
                    <img src={subscribeData.imgurl} alt="Subscribe" className="w-full h-full object-contain" />
                  </div>
                )}

                {/* Text — heading line + tagline (Figma: middle column) */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-1.5">
                    <SectionHeading text={subscribeData.heading} className="!text-[20px] md:!text-[24px] xl:!text-[28px]" />
                    {subscribeData.subheading2 && (
                      <span className="text-[15px] md:text-[17px] xl:text-[19px] font-semibold text-[#1A1A2E]">
                        {subscribeData.subheading2}
                      </span>
                    )}
                  </div>
                  {subscribeData.subheading && (
                    <p
                      className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] md:text-[15px] xl:text-[16px] font-medium"
                      style={{ color: 'var(--color-brand-primary)' }}
                    >
                      {subscribeData.subheading.split('|').map((part, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="w-px h-4 bg-[#004276] opacity-70" aria-hidden="true" />}
                          <span>{part.trim()}</span>
                        </React.Fragment>
                      ))}
                    </p>
                  )}
                </div>

                {/* Actions — stacked, equal width (Figma: right column) */}
                <div className="flex flex-col items-stretch gap-2 w-full md:w-[190px] flex-shrink-0">
                  {subscribeData.button && (
                    <button
                      onClick={handleSubscribe}
                      className="h-9 md:h-10 px-6 rounded-full text-white text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
                      style={{ background: 'var(--color-brand-primary)' }}
                    >
                      {hasLatestSubscription ? 'Renew Now' : subscribeData.button}
                    </button>
                  )}
                  {subscribeData.button1 && (
                    <a
                      href={`https://wa.me/91${phoneNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-9 md:h-10 px-5 rounded-full text-sm font-semibold border border-[#004276] text-[#004276] bg-white flex items-center justify-center gap-2 hover:bg-[#EAF3FA] transition-colors whitespace-nowrap"
                    >
                      <span className="flex" style={{ color: '#25D366' }}><WhatsAppIcon /></span>
                      {subscribeData.button1}
                    </a>
                  )}
                  {subscribeData.callus && (
                    <button
                      onClick={handleCallUs}
                      className="flex items-center justify-center gap-2 text-sm font-semibold text-[#1A1A2E] hover:opacity-75 transition-opacity whitespace-nowrap"
                    >
                      <PhoneIcon />
                      {subscribeData.callus}
                    </button>
                  )}
                </div>
              </div>
            )}

          </div>
        </section>
      )}

      {Object.keys(sixthSection).length > 0 && (
        <CurebaySolutions
          data={sixthSection}
          onCardClick={handleCardClick}
        />
      )}

      {/* FAQ section */}
      {Object.keys(faqSection).length > 0 && (faqSection.subheading || []).length > 0 && (() => {
        const tabs = faqSection.subheading || [];
        const activeTab = tabs[faqTabIdx] || tabs[0];
        const questions = (activeTab?.subcomp || []).filter((q) => q.question);

        return (
          <section className={`${SECTION_PAD} bg-white`}>
            <div className="w-full">
              {/* Heading */}
              {faqSection.heading && (
                <SectionHeading text={faqSection.heading} className="mb-5 md:mb-8" />
              )}

              {/* Tabs — spread across the full width on desktop, scrollable on mobile */}
              <div className="border-b border-gray-200 mb-4 md:mb-6 overflow-x-auto overflow-y-hidden">
                <div className="flex min-w-max md:min-w-0 md:w-full">
                  {tabs.map((tab, i) => {
                    const isActive = i === faqTabIdx;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setFaqTabIdx(i)}
                        className={`relative md:flex-1 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm whitespace-nowrap transition-colors ${i < tabs.length - 1 ? 'border-r border-gray-200' : ''}`}
                        style={
                          isActive
                            ? {
                                background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)',
                                color: 'var(--color-brand-primary)',
                                fontWeight: 600,
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                              }
                            : { color: '#4B5563' }
                        }
                      >
                        {tab.subheading}
                        {isActive && (
                          <span
                            className="absolute left-3 right-3 -bottom-[1px] h-[3px] rounded-full"
                            style={{ background: 'var(--color-brand-green)' }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Questions */}
              {questions.length > 0 ? (
                <div className="flex flex-col gap-3 md:gap-4">
                  {questions.map((qa) => {
                    const isOpen = openFaqId === qa.id;
                    return (
                      <div
                        key={qa.id}
                        className="rounded-xl overflow-hidden transition-colors"
                        style={{ background: isOpen ? '#EAF3FA' : '#F1F8FD' }}
                      >
                        <button
                          onClick={() => setOpenFaqId(isOpen ? null : qa.id)}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3.5 md:px-5 md:py-4 text-left"
                        >
                          <span className="text-sm md:text-[15px] font-medium" style={{ color: 'var(--color-brand-primary)' }}>{qa.question}</span>
                          <svg
                            width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="#004276" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                            className={`transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                          >
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                        </button>
                        {isOpen && qa.answer && (
                          <div className="px-4 pb-4 md:px-5 md:pb-5 pt-0">
                            <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{qa.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-sm text-gray-400 py-10">No questions available in this category yet.</p>
              )}
            </div>
          </section>
        );
      })()}

      
    </section>

    
    {isSmartOpen && (
      <CaresathiCreate
        userData={userData}
        initialVisible={isSmartOpen}
        onCloseInitial={handleSmartClose}
      />
    )}

    {/* Subscription Dialog */}
    <SubscriptionDialog
      visible={subscribeVisible}
      onHide={() => setSubscribeVisible(false)}
      userData={userData}
    />

    {/* Sticky Subscribe CTA at bottom */}
    {/* <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-gray-100 shadow-lg py-3 px-4">
      <div className="max-w-lg mx-auto flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 truncate">
            24×7 healthcare companion for you & your family
          </p>
        </div>
        <button
          onClick={handleSubscribe}
          className="flex-shrink-0 bg-gradient-to-r from-[#00BD7F] to-[#00B5E2] text-white text-sm font-bold
            px-6 py-2.5 rounded-full shadow-md hover:opacity-90 active:scale-95 transition-all duration-150 whitespace-nowrap"
        >
          Subscribe Now
        </button>
      </div>
    </div> */}
  {showFeedback && (
    <FeedbackModal service="Care Sathi" patientId={userData?.code} onClose={closeFeedback} />
  )}
  </>
);
};

export default Caresathi;
